import {
  AuthMethodRegistrationRequiredState,
  AuthMethodVerificationRequiredState,
  CustomAuthPublicClientApplication,
  MfaAwaitingState,
  MfaVerificationRequiredState,
  SignInCodeRequiredState,
  SignInPasswordRequiredState,
  SignUpAttributesRequiredState,
  SignUpCodeRequiredState,
  SignUpCompletedState,
  SignUpPasswordRequiredState
} from "@azure/msal-browser/custom-auth";

export class NativeAuthController {
  constructor(config, apiScope, callbacks) {
    this.config = config;
    this.apiScope = apiScope;
    this.callbacks = callbacks;
    this.client = null;
    this.accountData = null;
    this.flowState = null;
    this.username = "";
    this.mode = "sign-in";
    this.requiredAttributes = [];
    this.passkeyClaims = JSON.stringify({
      access_token: {
        xms_cc: { values: ["cp1"] },
        acrs: {
          essential: true,
          value: this.config.passkeyMfaAuthContext || "c1"
        }
      }
    });
  }

  async initialize() {
    this.client = await CustomAuthPublicClientApplication.create({
      customAuth: {
        challengeTypes: ["password", "oob", "redirect"],
        capabilities: ["mfa_required", "registration_required"],
        authApiProxyUrl: this.config.nativeAuthApiUrl
      },
      auth: {
        clientId: this.config.clientId,
        authority: `https://${this.config.tenantSubdomain}.ciamlogin.com`,
        redirectUri: this.config.redirectUri,
        postLogoutRedirectUri: this.config.redirectUri
      },
      cache: {
        cacheLocation: "sessionStorage"
      }
    });

    const accountResult = this.client.getCurrentAccount();
    if (accountResult.isCompleted()) {
      this.complete(accountResult.data);
      return true;
    }

    return false;
  }

  showInitial(mode = "sign-in") {
    this.mode = mode;
    this.flowState = null;
    this.callbacks.onPrompt(mode === "sign-up"
      ? {
          mode,
          title: "Join the Bulldog Pints pack",
          description: "Create an account without leaving the storefront.",
          fields: [
            { name: "displayName", label: "Display name", autocomplete: "name" },
            { name: "email", label: "Email address", type: "email", autocomplete: "email" },
            { name: "password", label: "Password", type: "password", autocomplete: "new-password" },
            { name: "confirmPassword", label: "Confirm password", type: "password", autocomplete: "new-password" }
          ],
          submitLabel: "Create account"
        }
      : {
          mode,
          title: "Native sign in",
          description: "Sign in directly inside Bulldog Pints.",
          fields: [
            { name: "email", label: "Email address", type: "email", autocomplete: "email" }
          ],
          submitLabel: "Continue"
        });
  }

  async submit(values) {
    this.callbacks.onBusy(true);
    this.callbacks.onError(null);

    try {
      if (!this.flowState) {
        if (this.mode === "sign-up") {
          await this.startSignUp(values);
        } else {
          await this.startSignIn(values);
        }
        return;
      }

      let result;
      if (this.flowState instanceof SignInPasswordRequiredState ||
          this.flowState instanceof SignUpPasswordRequiredState) {
        result = await this.flowState.submitPassword(values.password);
      } else if (this.flowState instanceof SignInCodeRequiredState ||
                 this.flowState instanceof SignUpCodeRequiredState) {
        result = await this.flowState.submitCode(values.code);
      } else if (this.flowState instanceof SignUpAttributesRequiredState) {
        result = await this.flowState.submitAttributes(
          Object.fromEntries(this.requiredAttributes.map(attribute => [
            attribute.name,
            values[attribute.name].trim()
          ]))
        );
      } else if (this.flowState instanceof MfaVerificationRequiredState ||
                 this.flowState instanceof AuthMethodVerificationRequiredState) {
        result = await this.flowState.submitChallenge(values.code);
      } else if (this.flowState instanceof AuthMethodRegistrationRequiredState) {
        const method = this.flowState.getAuthMethods()[0];
        if (!method) throw new Error("No authentication method is available for registration.");
        result = await this.flowState.challengeAuthMethod({
          authMethodType: method,
          verificationContact: values.contact
        });
      } else {
        throw new Error("The native authentication flow is not in a submittable state.");
      }

      await this.processResult(result);
    } catch (error) {
      console.error(error);
      this.callbacks.onError(createNativeErrorExperience(error));
    } finally {
      this.callbacks.onBusy(false);
    }
  }

  async startSignIn(values) {
    this.username = values.email.trim();
    const result = await this.client.signIn({
      username: this.username,
      scopes: this.scopes,
      claims: this.passkeyClaims
    });
    await this.processResult(result);
  }

  async startSignUp(values) {
    if (values.password !== values.confirmPassword) {
      throw new Error("Passwords do not match.");
    }

    this.username = values.email.trim();
    const result = await this.client.signUp({
      username: this.username,
      password: values.password,
      attributes: {
        displayName: values.displayName.trim()
      }
    });
    await this.processResult(result);
  }

  async processResult(result) {
    if (typeof result.isFailed === "function" && result.isFailed()) {
      if (result.error?.isRedirectRequired?.()) {
        this.flowState = null;
        await this.callbacks.onRedirectRequired(this.username);
        return;
      }
      throw result.error ?? new Error("Native authentication failed.");
    }

    this.flowState = result.state;

    if (this.flowState instanceof SignUpCompletedState) {
      const signInResult = await this.flowState.signIn({
        scopes: this.scopes,
        claims: this.passkeyClaims
      });
      await this.processResult(signInResult);
      return;
    }

    if (typeof result.isCompleted === "function" && result.isCompleted()) {
      const accountData = result.data ?? this.getCompletedAccount();
      if (!accountData) throw new Error("Authentication completed without an account.");
      this.complete(accountData);
      return;
    }

    if (this.flowState instanceof MfaAwaitingState) {
      const method = this.flowState.getAuthMethods()[0];
      if (!method) throw new Error("No MFA method is available.");
      const challengeResult = await this.flowState.requestChallenge(method.id);
      await this.processResult(challengeResult);
      return;
    }

    this.promptForState();
  }

  promptForState() {
    if (this.flowState instanceof SignInPasswordRequiredState ||
        this.flowState instanceof SignUpPasswordRequiredState) {
      this.callbacks.onPrompt({
        mode: this.mode,
        title: "Enter your password",
        description: `Continue as ${this.username}.`,
        fields: [{ name: "password", label: "Password", type: "password", autocomplete: "current-password" }],
        submitLabel: "Continue"
      });
      return;
    }

    if (this.flowState instanceof SignInCodeRequiredState ||
        this.flowState instanceof SignUpCodeRequiredState) {
      this.callbacks.onPrompt(codePrompt(this.mode, "Enter the verification code sent to your email."));
      return;
    }

    if (this.flowState instanceof SignUpAttributesRequiredState) {
      this.requiredAttributes = this.flowState.getRequiredAttributes();
      this.callbacks.onPrompt({
        mode: this.mode,
        title: "Complete your profile",
        description: "Provide the account details required by Bulldog Pints.",
        fields: this.requiredAttributes.map(attribute => ({
          name: attribute.name,
          label: formatAttributeLabel(attribute.name),
          type: attribute.name.toLowerCase().includes("email") ? "email" : "text",
          autocomplete: attributeAutocomplete(attribute.name)
        })),
        submitLabel: "Continue"
      });
      return;
    }

    if (this.flowState instanceof MfaVerificationRequiredState) {
      this.callbacks.onPrompt(codePrompt(
        this.mode,
        `Enter the ${this.flowState.getCodeLength()}-digit code sent to ${this.flowState.getSentTo()}.`
      ));
      return;
    }

    if (this.flowState instanceof AuthMethodRegistrationRequiredState) {
      this.callbacks.onPrompt({
        mode: this.mode,
        title: "Register email MFA",
        description: "Confirm the email address to use for future verification.",
        fields: [{
          name: "contact",
          label: "Verification email",
          type: "email",
          autocomplete: "email",
          value: this.username
        }],
        submitLabel: "Send code"
      });
      return;
    }

    if (this.flowState instanceof AuthMethodVerificationRequiredState) {
      this.callbacks.onPrompt(codePrompt(
        this.mode,
        `Enter the ${this.flowState.getCodeLength()}-digit code sent to ${this.flowState.getSentTo()}.`
      ));
      return;
    }

    throw new Error(`Unsupported native authentication state: ${this.flowState?.constructor?.name ?? "unknown"}.`);
  }

  complete(accountData) {
    this.accountData = accountData;
    this.flowState = null;
    this.callbacks.onAuthenticated(accountData);
  }

  getCompletedAccount() {
    const accountResult = this.client.getCurrentAccount();
    return accountResult.isCompleted() ? accountResult.data : null;
  }

  async getAccessToken() {
    if (!this.accountData) return null;
    const result = await this.accountData.getAccessToken({
      forceRefresh: false,
      scopes: [this.apiScope]
    });
    if (result.isFailed()) throw result.error;
    return result.data;
  }

  async signOut() {
    if (this.accountData) {
      const result = await this.accountData.signOut();
      if (result.isFailed()) throw result.error;
    }
    this.accountData = null;
    this.flowState = null;
    this.callbacks.onSignedOut();
  }

  get isSignedIn() {
    return Boolean(this.accountData);
  }

  get account() {
    return this.accountData?.getAccount() ?? null;
  }

  get scopes() {
    return ["openid", "profile", "offline_access", "email", this.apiScope];
  }
}

function codePrompt(mode, description) {
  return {
    mode,
    title: "Check your email",
    description,
    fields: [{
      name: "code",
      label: "Verification code",
      inputMode: "numeric",
      autocomplete: "one-time-code"
    }],
    submitLabel: "Verify"
  };
}

export function createNativeErrorExperience(error) {
  const data = error?.errorData;
  const searchableText = [
    data?.error,
    data?.errorCode,
    data?.errorDescription,
    error?.errorCode,
    error?.subError,
    error?.message
  ].filter(Boolean).join(" ").toLowerCase();

  let title = "We couldn't complete that step";
  let message = "Try again. If the problem continues, expand the technical details below.";

  if (searchableText.includes("passwords do not match")) {
    title = "Your passwords don't match";
    message = "Enter the same password in both password fields.";
  } else if (searchableText.includes("invalid_grant") ||
             searchableText.includes("invalid username or password") ||
             searchableText.includes("invalid credentials")) {
    title = "We couldn't sign you in";
    message = "Check your email address and password, then try again.";
  } else if (searchableText.includes("already exists") ||
             searchableText.includes("user_already_exists")) {
    title = "An account already uses that email";
    message = "Return to sign in and use your existing account.";
  } else if (searchableText.includes("password") &&
             (searchableText.includes("complex") ||
              searchableText.includes("weak") ||
              searchableText.includes("policy"))) {
    title = "Choose a stronger password";
    message = "Use a longer password with a mix of uppercase and lowercase letters, numbers, and symbols.";
  } else if (searchableText.includes("1003002")) {
    title = "We couldn't finish signing you in";
    message = "The token service extension did not return a valid response. Try again after the service is restored.";
  } else if ([
    "invalid_code",
    "code_invalid",
    "otp_invalid",
    "verification code is invalid",
    "verification code has expired",
    "incorrect verification code"
  ].some(value => searchableText.includes(value))) {
    title = "That verification code didn't work";
    message = "Check the code, request a new one if needed, and try again.";
  } else if (searchableText.includes("throttl") ||
             searchableText.includes("too many") ||
             searchableText.includes("temporarily blocked")) {
    title = "Too many attempts";
    message = "Wait a few minutes before trying again.";
  } else if (searchableText.includes("network") ||
             searchableText.includes("failed to fetch") ||
             searchableText.includes("connection")) {
    title = "We couldn't reach the sign-in service";
    message = "Check your connection and try again.";
  }

  return {
    title,
    message,
    details: stringifyNativeError(error)
  };
}

function stringifyNativeError(error) {
  if (!error || typeof error !== "object") return String(error);

  const payload = { ...error };
  for (const property of [
    "name",
    "message",
    "errorCode",
    "subError",
    "correlationId",
    "traceId",
    "timestamp",
    "status",
    "errorData"
  ]) {
    if (error[property] !== undefined) payload[property] = error[property];
  }

  const seen = new WeakSet();
  return JSON.stringify(payload, (_key, value) => {
    if (typeof value !== "object" || value === null) return value;
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    return value;
  }, 2);
}

function formatAttributeLabel(name) {
  return name
    .replace(/^extension_[^_]+_/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
}

function attributeAutocomplete(name) {
  const normalized = name.toLowerCase();
  if (normalized === "displayname") return "name";
  if (normalized === "givenname") return "given-name";
  if (normalized === "surname") return "family-name";
  if (normalized.includes("email")) return "email";
  if (normalized.includes("postal")) return "postal-code";
  if (normalized.includes("country")) return "country-name";
  if (normalized.includes("street")) return "street-address";
  return "off";
}
